import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { postLogin } from '@/api/auth/postLogin';
import { Images } from '@/assets';
import Button from '@/components/button/Button';
import Input from '@/components/input/Input';
import { useAuth } from '@/context/AuthContext';
import { useAlert } from '@/hooks/useAlert';
import { useAppDispatch, useAppSelector } from '@/stores/hooks';
import { auth } from '@/stores/slices/authSlices';
import { colors } from '@/theme/colors';
import { EMAIL_REGEX, PASSWORD_REGEX } from '@/utils/regex';

import AuthLinks from './AuthLinks';
import LoginBanner from './LoginBanner';
import { Helmet } from 'react-helmet-async';
import { langPath } from '@/utils/langPath';
import LanguageButtons from '@/components/header/LanguageButtons';

const LoginPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [emailValue, setEmailValue] = useState<string>('');
  const [passwordValue, setPasswordValue] = useState<string>('');
  const [emailError, setEmailError] = useState<string>('');
  const [passwordError, setPasswordError] = useState<string>('');
  const dispatch = useAppDispatch();
  const { loginRequest } = useAppSelector((state) => state.auth);
  const { login, isLoggedIn, roleType } = useAuth();
  const { showAlert } = useAlert();

  const onChangeEmail = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setEmailValue(value);
      dispatch(auth.setInputLoginId(value));
      if (value.trim() === '') {
        setEmailError(t('pleaseEnterTheEmail'));
      } else if (!EMAIL_REGEX.test(value)) {
        setEmailError(t('pleaseEnterAvalidEmailAddress'));
      } else {
        setEmailError('');
      }
    },
    [dispatch, t]
  );

  const onChangePassword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setPasswordValue(value);
      dispatch(auth.setInputLoginPassword(value));
      if (value.trim() === '') {
        setPasswordError(t('pleaseEnterYourPassword'));
      } else if (!PASSWORD_REGEX.test(value)) {
        setPasswordError(t('pleaseEnterAcombination'));
      } else {
        setPasswordError('');
      }
    },
    [dispatch, t]
  );

  const validateEmail = useCallback(() => {
    if (emailValue.trim() === '') {
      setEmailError(t('pleaseEnterYourEmail'));
      return false;
    } else if (!EMAIL_REGEX.test(emailValue)) {
      setEmailError(t('pleaseEnterAvalidEmailAddress'));
      return false;
    } else {
      setEmailError('');
      return true;
    }
  }, [emailValue, t]);

  const validatePassword = useCallback(() => {
    if (passwordValue.trim() === '') {
      setPasswordError(t('pleaseEnterYourPassword'));
      return false;
    } else if (!PASSWORD_REGEX.test(passwordValue)) {
      setPasswordError(t('pleaseEnterAcombination'));
      return false;
    } else {
      setPasswordError('');
      return true;
    }
  }, [passwordValue, t]);

  const onBlurEmail = () => {
    validateEmail();
  };

  const onBlurPassword = () => {
    validatePassword();
  };

  const buttonActive = useMemo(() => {
    const isEmailValid = emailValue.trim() !== '' && emailError === '';
    const isPasswordValid = passwordValue.trim() !== '' && passwordError === '';
    return isEmailValid && isPasswordValid;
  }, [emailValue, passwordValue, emailError, passwordError]);

  const onClickLogin = useCallback(async () => {
    const data = await postLogin(loginRequest);
    if (data.isSuccess) {
      const loginValid =
        data.data.role === 'CENTER' ||
        data.data.role === 'PLANNER' ||
        data.data.role === 'MGG_CENTER';
      if (data.data.role === 'PLAYER') {
        showAlert({
          message: t('thisLoginIsForPartnerOnlyToPlayer'),
        });
        return;
      } else if (!loginValid) {
        showAlert({
          message: t('thisLoginIsForPartnerOnly'),
        });
        return;
      }
      dispatch(auth.setLoginResponse(data.data));
      dispatch(auth.setShouldCheckBusinessProfile(true));
      dispatch(auth.setShouldCheckServiceAdd(true));
      login(data.data.accessToken, data.data.role);
    } else {
      if (data.error.code === 'E_LOGIN_003') {
        showAlert({ message: t('invalidEmailOrPassword') });
      } else {
        showAlert({ message: t('theInputValueIsNotValid') });
      }
    }
  }, [loginRequest, dispatch, login, showAlert, t]);

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && buttonActive) {
        onClickLogin();
      }
    },
    [buttonActive, onClickLogin]
  );

  useEffect(() => {
    if (isLoggedIn && roleType !== 'PLAYER') {
      navigate(langPath('/main'), { replace: true });
    }
  }, [isLoggedIn, roleType, navigate]);

  return (
    <>
      <Helmet>
        <title>Partner Login</title>
      </Helmet>
      <Layout>
        <LoginBanner />
        <Container>
          <Section>
            <div>
              <img src={Images.R2PlaygroundLogo} />
            </div>
            <Wrapper onKeyDown={onKeyDown}>
              <InputSection>
                <Input
                  id="email"
                  label={t('email')}
                  type="email"
                  value={emailValue}
                  placeholder={t('enterTheEmailToBeUsedAsYourId')}
                  errorMessage={emailError}
                  onChange={onChangeEmail}
                  onBlur={onBlurEmail}
                />
                <Input
                  id="password"
                  label={t('password')}
                  type="password"
                  value={passwordValue}
                  placeholder={t('pleaseEnterAcombinationOfInput')}
                  errorMessage={passwordError}
                  onChange={onChangePassword}
                  onBlur={onBlurPassword}
                />
              </InputSection>
              <ButtonSection>
                <Button
                  text={t('login')}
                  disabled={!buttonActive}
                  onClick={onClickLogin}
                  size="large"
                />
                <AuthLinks />
              </ButtonSection>
            </Wrapper>
          </Section>
        </Container>
        <HeaderContainer>
          <LanguageButtons />
        </HeaderContainer>
      </Layout>
    </>
  );
};

export default LoginPage;

const Layout = styled.div`
  width: 100vw;
  height: 100vh;
  background: url(${Images.LoginBackground});
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 50px;
  position: relative;
`;

const Container = styled.div`
  max-width: 1000px;
  width: 100%;
  box-shadow: 0px 5px 40px 0px ${colors.boxShadow};
  background-color: ${colors.white};
  padding: 60px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const Section = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 520px;
  width: 100%;
  gap: 20px;
`;

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const InputSection = styled.div`
  display: flex;
  flex-direction: column;
  border-bottom: 1px solid ${colors.divider};
  padding-bottom: 24px;
  gap: 5px;
`;

const ButtonSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 40px;
`;

const HeaderContainer = styled.div`
  position: absolute;
  top: 30px;
  right: 50px;
`;
